import { StandardEvents } from '@shopify/events';

class CustomPendantEngravingProduct extends HTMLElement {
  connectedCallback() {
    this.galleryImage = this.querySelector('[data-pep-main-image]');
    this.galleryButtons = this.querySelectorAll('[data-pep-thumb]');
    this.galleryMedia = this.querySelector('.pep__main-media');
    this.galleryContent = this.querySelector('[data-pep-main-media-content]');
    this.galleryProgress = this.querySelector('[data-pep-media-progress]');
    this.currentMediaIndex = Math.max(0, Array.from(this.galleryButtons).findIndex((button) => button.getAttribute('aria-current') === 'true'));
    this.variantInputs = this.querySelectorAll('[data-pep-option]');
    this.variantIdInputs = this.querySelectorAll('[data-pep-variant-id]');
    this.variantData = this.parseJson('[data-pep-variants]');
    this.fontDrawer = this.querySelector('[data-pep-font-drawer]');
    this.optionDrawers = this.querySelectorAll('[data-pep-option-drawer]');
    this.advisorDrawer = this.querySelector('[data-pep-drawer]');
    this.openFontButtons = this.querySelectorAll('[data-pep-open-font]');
    this.closeFontButtons = this.querySelectorAll('[data-pep-close-font]');
    this.openOptionButtons = this.querySelectorAll('[data-pep-open-option-drawer]');
    this.closeOptionButtons = this.querySelectorAll('[data-pep-close-option-drawer]');
    this.openAdvisorButtons = this.querySelectorAll('[data-pep-open-advisor]');
    this.closeAdvisorButtons = this.querySelectorAll('[data-pep-close-advisor]');
    this.fontList = this.querySelector('[data-pep-font-list]');
    this.fontStyleBlocks = this.querySelectorAll('[data-pep-font-style-block]');
    this.fontInputs = this.querySelectorAll('[data-pep-font-option]');
    this.fontProperty = this.querySelector('[data-pep-font-property]');
    this.fontNameTargets = this.querySelectorAll('[data-pep-font-name]');
    this.selectedFontPreviewTargets = this.querySelectorAll('[data-pep-selected-font-preview]');
    this.fontOptionPreviewTargets = this.querySelectorAll('[data-pep-font-option-preview]');
    this.selectedFontCircleTargets = this.querySelectorAll('[data-pep-selected-font-circle]');
    this.engravingInput = this.querySelector('[data-pep-engraving-input]');
    this.characterCount = this.querySelector('[data-pep-character-count]');
    this.exampleTabs = this.querySelectorAll('[data-pep-example-tab]');
    this.examplePanels = this.querySelectorAll('[data-pep-example-panel]');
    this.accordions = this.querySelectorAll('[data-pep-accordion]');
    this.addButtons = this.querySelectorAll('[data-pep-add-button]');
    this.boundCartUpdate = (event) => this.handleCartUpdate(event);
    this.characterLimitMap = this.parseCharacterLimitMap(this.dataset.characterLimits || '');
    this.characterLimitOverride = this.parseCharacterLimitOverride(this.dataset.characterLimitOverride);

    this.galleryButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectMedia(button));
    });

    this.updateGalleryProgress();

    if (this.galleryMedia && this.galleryButtons.length > 1) {
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.isSwiping = false;

      this.galleryMedia.addEventListener('touchstart', (event) => this.handleTouchStart(event), { passive: true });
      this.galleryMedia.addEventListener('touchmove', (event) => this.handleTouchMove(event), { passive: true });
      this.galleryMedia.addEventListener('touchend', (event) => this.handleTouchEnd(event));
    }

    this.variantInputs.forEach((input) => {
      input.addEventListener('change', () => {
        this.updateVariant();
        this.closeDrawer(input.closest('[data-pep-option-drawer]'), 'custom-pendant-engraving-option-open');
      });
    });

    this.openFontButtons.forEach((button) => {
      button.addEventListener('click', () => this.openDrawer(this.fontDrawer, 'custom-pendant-engraving-font-open'));
    });

    this.closeFontButtons.forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer(button.closest('[data-pep-font-drawer]'), 'custom-pendant-engraving-font-open'));
    });

    this.openOptionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const drawer = this.querySelector(`[data-pep-option-drawer="${button.dataset.pepOpenOptionDrawer}"]`);
        this.openDrawer(drawer, 'custom-pendant-engraving-option-open');
      });
    });

    this.closeOptionButtons.forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer(button.closest('[data-pep-option-drawer]'), 'custom-pendant-engraving-option-open'));
    });

    this.openAdvisorButtons.forEach((button) => {
      button.addEventListener('click', () => this.openDrawer(this.advisorDrawer, 'custom-pendant-engraving-advisor-open'));
    });

    this.closeAdvisorButtons.forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer(button.closest('[data-pep-drawer]'), 'custom-pendant-engraving-advisor-open'));
    });

    this.fontList?.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.matches('[data-pep-font-option]')) return;

      this.updateFont(input);
      this.closeDrawer(this.fontDrawer, 'custom-pendant-engraving-font-open');
    });

    this.engravingInput?.addEventListener('input', () => this.updateEngravingPreview());

    this.exampleTabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectExample(tab));
    });

    this.accordions.forEach((accordion) => {
      const summary = accordion.querySelector('summary');
      summary?.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggleAccordion(accordion);
      });
    });

    this.addButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (!button.disabled) this.setAddToCartLoading(true);
      });
    });

    this.addEventListener('click', (event) => this.handleBackdropClick(event));
    this.addEventListener('keydown', (event) => this.handleKeydown(event));
    document.addEventListener(StandardEvents.cartLinesUpdate, this.boundCartUpdate);

    this.updateVariant();
    this.updateFont(this.querySelector('[data-pep-font-option]:checked'));
    this.updateEngravingPreview();
    this.openAdvisorAfterContactSubmit();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.boundCartUpdate);
  }

  parseJson(selector) {
    const script = this.querySelector(selector);
    if (!script) return [];

    try {
      return JSON.parse(script.textContent || '[]');
    } catch (error) {
      return [];
    }
  }

  handleTouchStart(event) {
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.isSwiping = false;
  }

  handleTouchMove(event) {
    if (!this.touchStartX) return;

    const deltaX = event.touches[0].clientX - this.touchStartX;
    const deltaY = event.touches[0].clientY - this.touchStartY;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      this.isSwiping = true;
    }
  }

  handleTouchEnd(event) {
    if (!this.isSwiping) {
      this.touchStartX = 0;
      this.touchStartY = 0;
      return;
    }

    const deltaX = event.changedTouches[0].clientX - this.touchStartX;
    const threshold = 40;

    if (deltaX > threshold) {
      this.swipeGallery(-1);
    } else if (deltaX < -threshold) {
      this.swipeGallery(1);
    }

    this.touchStartX = 0;
    this.touchStartY = 0;
    this.isSwiping = false;
  }

  swipeGallery(direction) {
    const buttons = Array.from(this.galleryButtons);
    if (!buttons.length) return;

    this.currentMediaIndex = (this.currentMediaIndex + direction + buttons.length) % buttons.length;
    this.selectMedia(buttons[this.currentMediaIndex]);
  }

  selectMedia(button, animate = true) {
    const src = button.getAttribute('data-pep-image');
    const srcset = button.getAttribute('data-pep-srcset');
    const alt = button.getAttribute('data-pep-alt') || '';
    const mediaTemplate = button.querySelector('[data-pep-media-template]');
    const buttons = Array.from(this.galleryButtons);
    const nextIndex = buttons.indexOf(button);
    let direction = nextIndex >= this.currentMediaIndex ? 1 : -1;

    if (this.currentMediaIndex === buttons.length - 1 && nextIndex === 0) direction = 1;
    if (this.currentMediaIndex === 0 && nextIndex === buttons.length - 1) direction = -1;

    if (mediaTemplate && this.galleryContent) {
      if (animate) {
        this.transitionGalleryContent(mediaTemplate, direction);
      } else {
        this.replaceGalleryContent(mediaTemplate);
      }
    } else if (animate) {
      this.transitionGalleryImage(src, srcset, alt, direction);
    } else {
      this.setGalleryImage(src, srcset, alt);
    }
    this.galleryButtons.forEach((thumb) => thumb.setAttribute('aria-current', 'false'));
    button.setAttribute('aria-current', 'true');

    this.currentMediaIndex = nextIndex;
    this.updateGalleryProgress();
  }

  replaceGalleryContent(mediaTemplate) {
    if (!this.galleryContent || !mediaTemplate) return;

    this.galleryContent.querySelectorAll('video').forEach((video) => video.pause());
    this.galleryContent.replaceChildren(mediaTemplate.content.cloneNode(true));
    this.syncActiveGalleryMedia();
  }

  transitionGalleryContent(mediaTemplate, direction = 1) {
    const canAnimate = window.matchMedia('(max-width: 1199px)').matches
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      && typeof this.galleryContent?.animate === 'function';

    if (!canAnimate || !this.galleryMedia) {
      this.replaceGalleryContent(mediaTemplate);
      return;
    }

    this.galleryMedia.querySelectorAll('[data-pep-transition-content]').forEach((content) => content.remove());
    this.galleryContent.getAnimations().forEach((animation) => animation.cancel());

    const outgoingContent = this.galleryContent.cloneNode(false);
    outgoingContent.removeAttribute('data-pep-main-media-content');
    outgoingContent.setAttribute('data-pep-transition-content', '');
    outgoingContent.append(...this.galleryContent.childNodes);
    Object.assign(outgoingContent.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      zIndex: '1',
      pointerEvents: 'none',
    });
    this.galleryMedia.append(outgoingContent);

    this.galleryContent.replaceChildren(mediaTemplate.content.cloneNode(true));
    this.syncActiveGalleryMedia();

    const offset = direction >= 0 ? 100 : -100;
    const timing = { duration: 360, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' };
    outgoingContent.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${-offset}%)` }], timing)
      .finished.catch(() => {}).finally(() => {
        outgoingContent.querySelectorAll('video').forEach((video) => video.pause());
        outgoingContent.remove();
      });
    this.galleryContent.animate([{ transform: `translateX(${offset}%)` }, { transform: 'translateX(0)' }], timing);
  }

  syncActiveGalleryMedia() {
    this.galleryImage = this.galleryContent?.querySelector('[data-pep-main-image]') || null;
    const activeVideo = this.galleryContent?.querySelector('video');

    if (activeVideo) {
      activeVideo.muted = true;
      activeVideo.defaultMuted = true;
      activeVideo.loop = true;
      activeVideo.playsInline = true;
      activeVideo.play().catch(() => {});
    }
  }

  transitionGalleryImage(src, srcset, alt, direction = 1) {
    const canAnimate = window.matchMedia('(max-width: 1199px)').matches
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      && typeof this.galleryImage?.animate === 'function';

    if (!canAnimate || !this.galleryMedia) {
      this.setGalleryImage(src, srcset, alt);
      return;
    }

    this.galleryMedia.querySelectorAll('[data-pep-transition-image]').forEach((image) => image.remove());
    this.galleryImage.getAnimations().forEach((animation) => animation.cancel());

    const outgoingImage = this.galleryImage.cloneNode(true);
    outgoingImage.removeAttribute('data-pep-main-image');
    outgoingImage.setAttribute('data-pep-transition-image', '');
    outgoingImage.setAttribute('aria-hidden', 'true');
    Object.assign(outgoingImage.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      zIndex: '1',
      pointerEvents: 'none',
      objectFit: 'cover',
    });
    this.galleryMedia.append(outgoingImage);

    this.setGalleryImage(src, srcset, alt);
    const offset = direction >= 0 ? 100 : -100;
    const timing = { duration: 360, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' };
    outgoingImage.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${-offset}%)` }], timing)
      .finished.catch(() => {}).finally(() => outgoingImage.remove());
    this.galleryImage.animate([{ transform: `translateX(${offset}%)` }, { transform: 'translateX(0)' }], timing);
  }

  updateGalleryProgress() {
    if (!this.galleryProgress) return;

    const count = this.galleryButtons.length;
    this.galleryProgress.hidden = count <= 1;
    this.galleryProgress.style.setProperty('--media-progress-count', Math.max(count, 1));
    this.galleryProgress.style.setProperty('--media-progress-index', Math.max(this.currentMediaIndex, 0));
  }

  setGalleryImage(src, srcset, alt) {
    if (!this.galleryImage || !src) return;
    this.galleryImage.setAttribute('src', src);
    if (srcset) this.galleryImage.setAttribute('srcset', srcset);
    this.galleryImage.setAttribute('alt', alt || '');
  }

  openDrawer(drawer, lockClass) {
    if (!drawer) return;
    drawer.setAttribute('open', '');
    document.documentElement.classList.add(lockClass);
    const firstFocusable = drawer.querySelector('input:not([type="hidden"]), textarea, button');
    window.setTimeout(() => firstFocusable?.focus(), 180);
  }

  closeDrawer(drawer, lockClass) {
    if (!drawer) return;
    drawer.removeAttribute('open');
    document.documentElement.classList.remove(lockClass);
  }

  handleBackdropClick(event) {
    if (!(event.target instanceof Element)) return;
    if (!event.target.matches('.pep__drawer-backdrop')) return;

    const drawer = event.target.closest('[data-pep-font-drawer], [data-pep-option-drawer], [data-pep-drawer]');
    const lockClass = drawer?.hasAttribute('data-pep-font-drawer')
      ? 'custom-pendant-engraving-font-open'
      : drawer?.hasAttribute('data-pep-option-drawer')
        ? 'custom-pendant-engraving-option-open'
      : 'custom-pendant-engraving-advisor-open';
    this.closeDrawer(drawer, lockClass);
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return;
    this.closeDrawer(this.fontDrawer, 'custom-pendant-engraving-font-open');
    this.closeOptionDrawers();
    this.closeDrawer(this.advisorDrawer, 'custom-pendant-engraving-advisor-open');
  }

  updateVariant() {
    const selectedVariant = this.getSelectedVariant();
    if (!selectedVariant) {
      this.handleUnavailableVariantCombination();
      return;
    }

    this.selectedVariant = selectedVariant;
    this.dataset.selectedVariantId = selectedVariant.id;
    this.syncInputsToVariant(selectedVariant);
    this.updateUrl(selectedVariant);

    this.variantIdInputs.forEach((input) => {
      input.value = selectedVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    this.querySelectorAll('product-form-component').forEach((form) => {
      form.dataset.currentVariantId = selectedVariant.id;
      form.dataset.quantityDefault = selectedVariant.quantityDefault || '1';
    });

    this.querySelectorAll('[data-pep-price]').forEach((price) => {
      price.classList.toggle('pep__price--sale', Boolean(selectedVariant.onSale));
      price.querySelectorAll('[data-pep-sale-price]').forEach((salePrice) => {
        salePrice.textContent = selectedVariant.price;
      });
      price.querySelectorAll('[data-pep-compare-price]').forEach((comparePrice) => {
        comparePrice.textContent = selectedVariant.compareAtPrice || '';
        comparePrice.classList.toggle('hidden', !selectedVariant.onSale);
      });
      price.querySelectorAll('[data-pep-sale-badge]').forEach((badge) => {
        const customBadgeText = badge.dataset.pepCustomBadgeText || '';
        const badgeText = !selectedVariant.available
          ? badge.dataset.pepSoldOutBadgeText || 'SOLD OUT'
          : selectedVariant.onSale
            ? badge.dataset.pepSaleBadgeText || 'SALE'
            : customBadgeText;
        badge.textContent = badgeText;
        Array.from(badge.classList).forEach((className) => {
          if (className.startsWith('badge-')) badge.classList.remove(className);
        });
        if (badgeText) badge.classList.add(`badge-${badgeText.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`);
        badge.classList.toggle('hidden', !badgeText);
      });
    });

    this.querySelectorAll('[data-pep-drawer-price]').forEach((price) => {
      price.textContent = selectedVariant.price;
    });

    this.querySelectorAll('[data-pep-drawer-variant]').forEach((title) => {
      title.textContent = selectedVariant.title === 'Default Title' ? '' : selectedVariant.title;
    });

    this.updateAdvisorFields(selectedVariant);

    this.addButtons.forEach((button) => {
      button.toggleAttribute('disabled', !selectedVariant.available);
      const text = button.querySelector('.add-to-cart-text__content span span');
      if (text) text.textContent = selectedVariant.available ? button.dataset.availableText : button.dataset.soldOutText;
    });

    this.querySelectorAll('add-to-cart-component').forEach((container) => {
      if (selectedVariant.cartImage) container.dataset.productVariantMedia = selectedVariant.cartImage;
    });

    if (selectedVariant.featuredImage) {
      const matchingThumb = Array.from(this.galleryButtons).find((thumb) => thumb.getAttribute('data-pep-image') === selectedVariant.featuredImage);

      if (matchingThumb) {
        this.selectMedia(matchingThumb, false);
      } else {
        this.setGalleryImage(selectedVariant.featuredImage, selectedVariant.featuredImageSrcset, selectedVariant.featuredImageAlt);
      }
    }

    this.updateFontStyles(selectedVariant);
    this.updateCharacterLimit(selectedVariant);
    this.updateOptionDisplays(selectedVariant);
  }

  getSelectedVariant() {
    if (!this.variantData.length) return null;

    const optionCount = Math.max(...this.variantData.map((variant) => variant.options?.length || 0));
    const selectedOptions = Array.from({ length: optionCount }, (_, index) => {
      return this.querySelector(`[data-pep-option][data-option-position="${index + 1}"]:checked`)?.value;
    });

    return this.variantData.find((variant) => {
      return variant.options.every((option, index) => {
        return this.normalizeOptionValue(option) === this.normalizeOptionValue(selectedOptions[index]);
      });
    }) || null;
  }

  normalizeOptionValue(value) {
    return String(value ?? '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  handleUnavailableVariantCombination() {
    this.selectedVariant = null;
    delete this.dataset.selectedVariantId;

    this.querySelectorAll('[data-pep-option][data-option-position]:checked').forEach((input) => {
      const position = input.dataset.optionPosition;
      this.querySelectorAll(`[data-pep-selected-option-value="${position}"]`).forEach((target) => {
        target.textContent = input.value;
      });

      const choiceIcon = input.closest('label')?.querySelector('[data-pep-choice-icon]');
      this.querySelectorAll(`[data-pep-selected-option-icon="${position}"]`).forEach((target) => {
        target.innerHTML = choiceIcon?.innerHTML || '';
      });
    });

    this.variantIdInputs.forEach((input) => {
      input.value = '';
    });

    this.querySelectorAll('product-form-component').forEach((form) => {
      form.dataset.currentVariantId = '';
    });

    this.addButtons.forEach((button) => {
      button.disabled = true;
      const text = button.querySelector('.add-to-cart-text__content span span');
      if (text) text.textContent = button.dataset.soldOutText || 'Unavailable';
    });
  }

  syncInputsToVariant(variant) {
    variant.options.forEach((value, index) => {
      const input = this.querySelector(
        `[data-pep-option][data-option-position="${index + 1}"][value="${this.escapeSelectorValue(value)}"]`
      );
      if (input) input.checked = true;
    });
  }

  updateOptionDisplays(variant) {
    if (!variant?.options) return;

    variant.options.forEach((value, index) => {
      const position = index + 1;
      this.querySelectorAll(`[data-pep-selected-option-value="${index + 1}"]`).forEach((target) => {
        target.textContent = value;
      });

      const input = this.querySelector(
        `[data-pep-option][data-option-position="${position}"][value="${this.escapeSelectorValue(value)}"]`
      );
      const choiceIcon = input?.closest('label')?.querySelector('[data-pep-choice-icon]');
      this.querySelectorAll(`[data-pep-selected-option-icon="${position}"]`).forEach((target) => {
        target.innerHTML = choiceIcon?.innerHTML || '';
      });
    });
  }

  closeOptionDrawers() {
    this.optionDrawers.forEach((drawer) => drawer.removeAttribute('open'));
    document.documentElement.classList.remove('custom-pendant-engraving-option-open');
  }

  updateCharacterLimit(variant) {
    if (!this.engravingInput) return;

    const nameLengthValue = this.querySelector('[data-pep-name-length-option] [data-pep-option]:checked')?.value;
    const optionText = variant.options.join(' ');
    const limit =
      this.getSelectedFontEngravingLimit() ??
      this.characterLimitOverride ??
      this.getLimitFromText(nameLengthValue) ??
      this.getLimitFromText(optionText) ??
      Number(this.engravingInput.dataset.defaultMax || 15);
    this.engravingInput.maxLength = limit;

    if (this.engravingInput.value.length > limit) {
      this.engravingInput.value = this.engravingInput.value.slice(0, limit);
    }

    this.updateEngravingPreview();
  }

  getSelectedFontEngravingLimit() {
    const selectedFont = this.querySelector('[data-pep-font-option]:checked');
    if (!selectedFont) return null;

    const limit = Number(selectedFont.dataset.fontEngravingLimit || '');
    return Number.isFinite(limit) && limit > 0 ? limit : null;
  }

  getLimitFromText(text) {
    const lowerText = String(text || '').toLowerCase();
    const matchedLimit = this.characterLimitMap.find(({ name }) => {
      return name && (lowerText.includes(name) || name.includes(lowerText));
    });

    if (matchedLimit) return matchedLimit.limit;

    const rangeLimit = lowerText.match(/(\d+)\s*(?:-|–|—|to)\s*(\d+)\s*(letters|letter|characters|character|chars|char)?/);
    if (rangeLimit) return Number(rangeLimit[2]);

    const explicitLimit = lowerText.match(/(\d+)\s*(characters|character|chars|char)/);
    if (explicitLimit) return Number(explicitLimit[1]);

    const letterLimit = lowerText.match(/(\d+)\s*(letters|letter)/);
    if (letterLimit) return Number(letterLimit[1]);

    return null;
  }

  parseCharacterLimitMap(value) {
    return String(value || '')
      .split(',')
      .map((pair) => {
        const [name, limit] = pair.split(':');
        return {
          name: String(name || '').trim().toLowerCase(),
          limit: Number(String(limit || '').trim()),
        };
      })
      .filter((item) => item.name && Number.isFinite(item.limit) && item.limit > 0);
  }

  parseCharacterLimitOverride(value) {
    if (value === undefined || value === null || value === '') return null;

    const limit = Number(value);
    return Number.isFinite(limit) && limit >= 0 ? limit : null;
  }

  updateFontStyles(variant) {
    const fontStyles = Array.isArray(variant?.fontStyles)
      ? variant.fontStyles.filter((font) => font?.name)
      : [];
    const hasFonts = fontStyles.length > 0;

    this.fontStyleBlocks.forEach((block) => {
      block.hidden = !hasFonts;
    });

    if (this.fontDrawer) {
      this.fontDrawer.hidden = !hasFonts;
      if (!hasFonts) this.closeDrawer(this.fontDrawer, 'custom-pendant-engraving-font-open');
    }

    if (this.fontProperty) {
      this.fontProperty.disabled = !hasFonts;
      if (!hasFonts) this.fontProperty.value = '';
    }

    if (!this.fontList || !hasFonts) {
      if (!hasFonts) this.fontList?.replaceChildren();
      this.fontInputs = this.querySelectorAll('[data-pep-font-option]');
      if (!hasFonts) this.updateEngravingPreview();
      return;
    }

    const previousFontName = this.querySelector('[data-pep-font-option]:checked')?.value || this.fontProperty?.value || '';
    this.fontList.replaceChildren(...fontStyles.map((font, index) => this.createFontOption(font, index, previousFontName)));
    this.fontInputs = this.querySelectorAll('[data-pep-font-option]');
    const checkedFont = this.querySelector('[data-pep-font-option]:checked') || this.fontInputs[0];

    if (checkedFont instanceof HTMLInputElement) {
      checkedFont.checked = true;
      this.updateFont(checkedFont, false);
    }
  }

  createFontOption(font, index, selectedFontName) {
    const fontName = String(font.name || 'Serif');
    const fontFamily = this.getFontFamily(fontName);
    const previewImage = String(font.previewImage || '');
    const label = document.createElement('label');
    label.className = 'pep__font-choice';

    const input = document.createElement('input');
    input.type = 'radio';
    input.name = `pep-font-${this.dataset.sectionId || 'product'}`;
    input.value = fontName;
    input.dataset.fontFamily = fontFamily;
    input.dataset.fontEngravingLimit = font.engravingLimit ?? '';
    input.dataset.fontPreviewImage = previewImage;
    input.setAttribute('data-pep-font-option', '');
    input.checked = selectedFontName ? fontName === selectedFontName : index === 0;

    const icon = document.createElement('span');
    icon.style.fontFamily = fontFamily;
    if (previewImage) {
      const image = document.createElement('img');
      image.src = previewImage;
      image.alt = fontName;
      image.loading = 'lazy';
      image.width = 100;
      image.height = 100;
      icon.append(image);
    } else {
      icon.textContent = 'Aa';
    }

    const labelText = document.createElement('strong');
    labelText.style.fontFamily = fontFamily;
    labelText.dataset.fontName = fontName;
    labelText.setAttribute('data-pep-font-option-preview', '');
    labelText.textContent = fontName;

    label.append(input, icon, labelText);
    this.loadFont(fontName);
    return label;
  }

  updateFont(input, updateLimit = true) {
    if (!input) return;

    const fontName = input.value;
    const fontFamily = input.dataset.fontFamily || this.getFontFamily(fontName);
    const fontPreviewImage = input.dataset.fontPreviewImage || '';
    if (this.fontProperty) this.fontProperty.value = fontName;

    this.fontNameTargets.forEach((target) => {
      target.textContent = fontName;
    });

    this.selectedFontPreviewTargets.forEach((target) => {
      target.style.fontFamily = fontFamily;
    });

    this.selectedFontCircleTargets.forEach((target) => {
      target.style.fontFamily = fontFamily;
      if (fontPreviewImage) {
        const image = document.createElement('img');
        image.src = fontPreviewImage;
        image.alt = fontName;
        image.width = 50;
        image.height = 50;
        target.replaceChildren(image);
      } else {
        target.textContent = 'Aa';
      }
    });

    if (updateLimit && this.selectedVariant) {
      this.updateCharacterLimit(this.selectedVariant);
    } else if (updateLimit) {
      this.updateEngravingPreview();
    }
  }

  getFontFamily(fontName) {
    const normalized = String(fontName || '').toLowerCase();
    if (normalized.includes('script')) return '"Great Vibes", cursive';
    if (normalized.includes('slab')) return '"Roboto Slab", serif';
    if (normalized.includes('sans')) return '"Montserrat", sans-serif';
    if (normalized === 'serif') return '"Cormorant Garamond", Georgia, serif';
    return `"${fontName}", serif`;
  }

  loadFont(fontName) {
    const normalized = String(fontName || '').trim();
    const normalizedLower = normalized.toLowerCase();
    const systemFonts = ['serif', 'sans serif', 'slab serif', 'script'];
    if (!normalized || systemFonts.includes(normalizedLower)) return;
    if (document.querySelector(`link[data-pep-font-link="${this.escapeSelectorValue(normalized)}"]`)) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(normalized).replace(/%20/g, '+')}:wght@400;500&display=swap`;
    link.dataset.pepFontLink = normalized;
    document.head.append(link);
  }

  updateEngravingPreview() {
    if (this.characterCount && this.engravingInput) {
      this.characterCount.textContent = `${this.engravingInput.value.length}/${this.engravingInput.maxLength}`;
    }

    const typedText = this.engravingInput?.value.trim();
    const selectedFont = this.querySelector('[data-pep-font-option]:checked');
    const fallbackText = selectedFont?.value || '';
    const previewText = typedText || fallbackText;

    this.selectedFontPreviewTargets.forEach((target) => {
      target.textContent = previewText;
    });

    this.fontOptionPreviewTargets.forEach((target) => {
      target.textContent = typedText || target.dataset.fontName || '';
    });
  }

  selectExample(tab) {
    const exampleId = tab.dataset.pepExampleTab;
    this.exampleTabs.forEach((item) => item.setAttribute('aria-selected', item === tab ? 'true' : 'false'));
    this.examplePanels.forEach((panel) => {
      panel.toggleAttribute('hidden', panel.dataset.pepExamplePanel !== exampleId);
    });
  }

  updateUrl(variant) {
    if (!variant?.id || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  updateAdvisorFields(variant) {
    const variantTitle = variant.title === 'Default Title' ? '' : variant.title;
    this.querySelectorAll('[data-pep-advisor-variant]').forEach((input) => {
      input.value = variantTitle;
    });
    this.querySelectorAll('[data-pep-advisor-url]').forEach((input) => {
      input.value = window.location.href;
    });
  }

  openAdvisorAfterContactSubmit() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('contact_posted') !== 'true') return;

    this.openDrawer(this.advisorDrawer, 'custom-pendant-engraving-advisor-open');
  }

  escapeSelectorValue(value) {
    if (window.CSS?.escape) return window.CSS.escape(value);
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  toggleAccordion(accordion) {
    if (accordion.open) {
      this.closeAccordion(accordion);
      return;
    }

    this.accordions.forEach((item) => {
      if (item !== accordion) this.closeAccordion(item);
    });
    this.openAccordion(accordion);
  }

  openAccordion(accordion) {
    const panel = accordion.querySelector('.pep__accordion-panel');
    if (!panel) return;

    accordion.open = true;
    panel.style.maxHeight = '0px';
    window.requestAnimationFrame(() => {
      panel.style.maxHeight = `${panel.scrollHeight}px`;
    });
  }

  closeAccordion(accordion) {
    const panel = accordion.querySelector('.pep__accordion-panel');
    if (!panel || !accordion.open) return;

    panel.style.maxHeight = `${panel.scrollHeight}px`;
    window.requestAnimationFrame(() => {
      panel.style.maxHeight = '0px';
    });

    const handleTransitionEnd = () => {
      accordion.open = false;
      panel.removeEventListener('transitionend', handleTransitionEnd);
    };

    panel.addEventListener('transitionend', handleTransitionEnd);
  }

  handleCartUpdate(event) {
    if (event.action !== 'add') return;

    const productForm = this.querySelector('product-form-component');
    if (event.target !== productForm && event.target !== this && !this.contains(event.target)) return;

    event.promise
      ?.then(({ detail }) => {
        this.setAddToCartLoading(false);
        if (detail?.didError) return;
        this.openCartDrawer();
      })
      .catch(() => {
        this.setAddToCartLoading(false);
      });
  }

  setAddToCartLoading(isLoading) {
    if (this.loadingTimeout) window.clearTimeout(this.loadingTimeout);

    this.addButtons.forEach((button) => {
      button.toggleAttribute('aria-busy', isLoading);
      button.dataset.loading = isLoading ? 'true' : 'false';
    });

    if (isLoading) {
      this.loadingTimeout = window.setTimeout(() => this.setAddToCartLoading(false), 8000);
    }
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('theme-drawer#cart-drawer');

    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    } else if (window.Shopify?.actions?.openCart) {
      window.Shopify.actions.openCart();
    }
  }
}

if (!customElements.get('custom-pendant-engraving-product')) {
  customElements.define('custom-pendant-engraving-product', CustomPendantEngravingProduct);
}
